import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const stats = [
  { k: "12,400+", v: "Users" },
  { k: "47.2M", v: "Emails deleted" },
  { k: "1.8M", v: "Hours saved" },
  { k: "AES-128", v: "Encryption" },
];

const testimonials = [
  {
    quote: "I deleted 38,000 emails in a single afternoon. My inbox finally feels mine again.",
    name: "Marcus K.", role: "Founder, Halo Labs",
    img: "https://images.pexels.com/photos/6146051/pexels-photo-6146051.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400",
  },
  {
    quote: "The auto-rules quietly cleaned 4 years of receipts. Like a Roomba for my inbox.",
    name: "Priya S.", role: "Operations, Northwind",
    img: "https://images.pexels.com/photos/20157010/pexels-photo-20157010.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400",
  },
];

function Header() {
  return (
    <header className="border-b border-border bg-background sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <img src="/easy-mail-logo.png" alt="Easy Mail" className="h-7 w-auto" />
          <span className="font-display font-bold text-lg">Easy Mail</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8 text-sm">
  <a href="#features" className="hover:text-primary">Features</a>
  <a href="#how" className="hover:text-primary">How it works</a>
  <a href="#pricing" className="hover:text-primary">Pricing</a>
  <a href="#providers" className="hover:text-primary">Supported Providers</a>
  <Link to="/legal" className="hover:text-primary">Legal Documents and Policies</Link>
</nav>


        {/* Actions */}
        <div className="flex items-center gap-3">
          <Link to="/login">
            <Button variant="ghost" className="rounded-none">Sign in</Button>
          </Link>

          <Link to="/register">
            <Button className="rounded-none btn-brutalist gap-2">
              Get started <ArrowRight size={16} />
            </Button>
          </Link>
        </div>

      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border mt-24">
      <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-2 lg:grid-cols-5 gap-8 text-sm">
        
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src="/easy-mail-logo.png" alt="Easy Mail" className="h-7 w-auto" />
            <span className="font-display font-bold">Easy Mail</span>
          </div>
          <p className="text-muted-foreground">
            Bulk email cleanup. Subscription management. Privacy by design.
          </p>
        </div>

        <div>
          <div className="section-tag mb-3">Product</div>
          <div className="space-y-2">
            <a href="#pricing" className="hover:text-primary">Pricing</a>
            <a href="#features" className="block hover:text-primary">Features</a>
            <Link to="/legal/privacy_policy" className="block hover:text-primary">Privacy Policy</Link>
            <Link to="/security" className="block hover:text-primary">Security</Link>
          </div>
        </div>

        <div>
          <div className="section-tag mb-3">Legal</div>
          <div className="space-y-2">
            <Link to="/legal/terms_and_conditions" className="block hover:text-primary">Terms &amp; Conditions</Link>
            <Link to="/data-deletion" className="block hover:text-primary">Data Deletion</Link>
            <Link to="/data-retention" className="block hover:text-primary">Data Retention</Link>
            <Link to="/gdpr" className="block hover:text-primary">GDPR & CCPA Rights</Link>
          </div>
        </div>

        <div>
          <div className="section-tag mb-3">Embed</div>
          <Link to="/embed/counter" className="block hover:text-primary">Live counter widget</Link>
        </div>

        <div>
          <div className="section-tag mb-3">Status</div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full" />
            All systems normal
          </div>
          <div className="text-xs text-muted-foreground mt-2 font-mono">GDPR · CCPA</div>
        </div>

      </div>

      <div className="border-t border-border py-4 text-xs text-muted-foreground text-center font-mono">
        © 2026 Easy Mail LLC. All rights reserved.
      </div>
    </footer>
  );
}

const providers = [
  { name: "Outlook", type: "OAuth", status: "live" },
  { name: "Hotmail", type: "OAuth", status: "live" },
  { name: "Yahoo", type: "IMAP", status: "live" },
  { name: "iCloud", type: "IMAP", status: "live" },
  { name: "AOL", type: "IMAP", status: "live" },
  { name: "Zoho", type: "IMAP", status: "live" },
  { name: "Fastmail", type: "IMAP", status: "live" },
  { name: "Proton", type: "IMAP", status: "live" },
  { name: "Gmail", type: "OAuth", status: "soon" },
];

function ProvidersTable() {
  return (
    <section id="providers" className="border-b border-border">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="section-tag mb-6">/ Supported Providers</div>
        <table className="w-full border border-border font-mono text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary">
              <th className="text-left px-4 py-3 font-semibold">Provider</th>
              <th className="text-left px-4 py-3 font-semibold">Connection Type</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {providers.map((p) => (
              <tr
                key={p.name}
                className={`border-b border-border ${p.status === "soon" ? "opacity-40" : ""}`}
              >
                <td className="px-4 py-3">{p.name}</td>
                <td className="px-4 py-3">{p.type}</td>
                <td className="px-4 py-3">
                  {p.status === "live" ? (
                    <span className="text-emerald-600 font-semibold">● LIVE</span>
                  ) : (
                    <span className="border border-border px-2 py-0.5 text-xs uppercase tracking-widest">Coming Soon</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="grid-bg border-b border-border">
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-32">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="section-tag mb-6">/ Inbox cleanup, industrialized</div>

              <h1 className="font-display text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tight">
                Clean 40,000<br />emails in one<br />
                <span className="text-primary">afternoon.</span>
              </h1>

              <p className="mt-8 text-lg text-foreground/80 max-w-xl">
                Easy Mail cleans your inbox through bulk deletion, subscription management,
                and filtered automated deletions when enabled — all without storing your email content.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-2 font-mono text-xs">
                <span className="text-muted-foreground uppercase tracking-widest mr-2">
                  Works with:
                </span>
                {["Outlook", "Hotmail", "Yahoo", "iCloud", "AOL", "Zoho", "Fastmail", "Proton"].map((p) => (
                  <span
                    key={p}
                    className="border border-border px-2 py-1 bg-background"
                  >
                    {p}
                  </span>
                ))}
                <span className="border border-border px-2 py-1 bg-background opacity-40">
                  Gmail (Coming Soon)
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
<section id="features" className="border-b border-border">
  <div className="max-w-7xl mx-auto px-6 py-20">
    <div className="section-tag mb-6">/ Features</div>

    <div className="grid md:grid-cols-3 gap-12">

      <div>
        <h3 className="font-display text-2xl mb-3">Bulk Delete Pass</h3>
        <p className="text-foreground/80">
          Delete thousands of emails by syncing your inbox with Bulk Delete Pass.
        </p>
      </div>

      <div>
        <h3 className="font-display text-2xl mb-3">Monthly Plan</h3>
        <p className="text-foreground/80">
          With a monthly plan you can enable filters that clean your inbox over time and cancel email subscriptions — no maintenance required.
        </p>
      </div>

      <div>
        <h3 className="font-display text-2xl mb-3">Special Rules</h3>
        <p className="text-foreground/80">
          Do both if you have purchased a Bulk Delete Pass previously and own a monthly plan.
        </p>
      </div>

      <div>
        <h3 className="font-display text-2xl mb-3">Zero content storage</h3>
        <p className="text-foreground/80">
          Easy Mail never stores your email content. Metadata is processed temporarily and deleted immediately.
        </p>
      </div>

      <div>
        <h3 className="font-display text-2xl mb-3">On‑demand processing</h3>
        <p className="text-foreground/80">
          Easy Mail acts only when you ask it to. Large user-requested bulk deletions may be queued on the server and continue after you leave the site, and optional background sync and auto-rules are disabled by default and only run if explicitly enabled in the service configuration.
        </p>
      </div>

      <div>
        <h3 className="font-display text-2xl mb-3">Multi‑provider support</h3>
        <p className="text-foreground/80">
          Outlook, Yahoo, iCloud, Zoho, AOL, Fastmail, Proton — with Gmail coming soon.
        </p>
      </div>

    </div>
  </div>
</section>


      {/* How It Works */}
      <section id="how" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="section-tag mb-6">/ How it works</div>

          <div className="grid md:grid-cols-3 gap-12">

            <div>
              <h3 className="font-display text-2xl mb-3">1. Connect your email</h3>
              <p className="text-foreground/80">
                OAuth for Outlook. App‑specific passwords for IMAP providers.
                Gmail support will be enabled after Google OAuth verification.
              </p>
            </div>

            <div>
              <h3 className="font-display text-2xl mb-3">2. Choose what to clean</h3>
              <p className="text-foreground/80">
                Select newsletters, promotions, unread messages, or custom filters.
              </p>
            </div>

            <div>
              <h3 className="font-display text-2xl mb-3">3. Clean instantly</h3>
              <p className="text-foreground/80">
                With a Bulk Delete Pass Easy Mail performs cleanup on‑demand — no syncing, no storage,
                no background access. With a monthly plan you can set up auto‑rules to keep your inbox clean going forward and manage subscriptions. If you have a monthly plan, and purchased bulk delete in the past, you have all the features!
              </p>
            </div>

          </div>
        </div>
      </section>
      {/* Pricing Preview */}
<section id="pricing" className="border-b border-border">
  <div className="max-w-7xl mx-auto px-6 py-20">
    <div className="section-tag mb-6">/ Pricing</div>

    <p className="text-foreground/80 max-w-2xl mb-12">
      Choose between a one-time Bulk Delete Pass for intensive cleanup, or monthly subscriptions for ongoing automated rules and inbox management.
    </p>

    <div className="grid md:grid-cols-2 gap-12">

      {/* Bulk Delete Pass */}
      <div className="border border-border p-8 bg-background">
        <h3 className="font-display text-3xl mb-3">Bulk Delete Pass</h3>
        <p className="text-foreground/80 mb-6">
          One-time 7-day access window for deep inbox cleanup. Perfect for large backlogs.
        </p>

        <ul className="space-y-2 text-sm text-foreground/80">
          <li>• Full bulk + filtered cleanup tools</li>
          <li>• Analytics during the access window</li>
          <li>• Bundles based on inbox count</li>
        </ul>

        <Link to="/pricing">
          <Button className="mt-6 w-full rounded-none btn-brutalist">
            View Bulk Delete bundles
          </Button>
        </Link>
      </div>

      {/* Monthly subscriptions */}
      <div className="border border-border p-8 bg-secondary">
        <h3 className="font-display text-3xl mb-3">Monthly subscriptions</h3>
        <p className="text-foreground/80 mb-6">
          Ongoing automated cleanup, auto-rules, and subscription management. Cancel any time.
        </p>

        <ul className="space-y-2 text-sm text-foreground/80">
          <li>• Automated rules and recurring cleanup</li>
          <li>• Multiple inboxes included per plan</li>
          <li>• Subscription management tools</li>
          <li>• (Special Access Rule) If purchased Bulk Delete before, a monthly plan reinstates the bulk delete + monthly features</li>
        </ul>

        <Link to="/pricing">
          <Button className="mt-6 w-full rounded-none btn-brutalist">
            View monthly plans
          </Button>
        </Link>
      </div>

    </div>
  </div>
</section>


      <ProvidersTable />

      <Footer />
    </div>
  );
}
